import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Avatar, Badge, Box, ButtonBase, Divider, Drawer, IconButton, ListItemIcon, Menu, MenuItem, Popover, Stack, Typography,
  useMediaQuery,
} from '@mui/material';
import {
  BuildOutlined, ExpandMore, Logout, Menu as MenuIcon, MapOutlined,
  NotificationsNoneOutlined, PaidOutlined, PeopleOutline, PrecisionManufacturingOutlined, ScaleOutlined, SettingsOutlined,
  ShoppingCartOutlined, StorefrontOutlined, WarehouseOutlined,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { RootState, AppDispatch } from '../store';
import { logout } from '../store/authSlice';
import SetPasswordOverlay from '../components/SetPasswordOverlay';
import { TONES } from '../components/ui/PageKit';
import { plantLayoutForTenant } from '../pages/factory/plant/layouts';
import { PlantDataProvider, findMachine, plantAlerts, shiftsToday, usePlantData } from '../pages/factory/plant/data';
import { ACCENT, ACCENT_SOFT, INK, LINE, MUTED, PulseDot, SURFACE } from '../pages/factory/plant/ui';
import { FlatNavContext } from '../pages/factory/shared';
import { CONFIG_PATHS } from '../pages/factory/FactoryConfigPage';

// Sistema de planta propio (hoy solo Parriplast / Asener): barra con la marca, tablero del día como inicio y un menú
// lateral por áreas. Cada ítem abre UNA pantalla: no hay solapas dentro de las pantallas (FlatNavContext) ni
// pestañas de sección. Lo que se configura una vez vive en /factory/config. El resto de los tenants sigue con AdminLayout.

const TOPBAR_H = 68;
const SIDEBAR_W = 248;

interface NavItem { label: string; path: string; also?: string[] }
interface NavGroup { id: string; label: string; icon: React.ReactNode; items: NavItem[] }

const PLANT_ITEM: NavItem = { label: 'Plano de planta', path: '/factory/plant' };

const GROUPS: NavGroup[] = [
  {
    id: 'production', label: 'Producción', icon: <PrecisionManufacturingOutlined />,
    items: [
      { label: 'Inyectoras en vivo', path: '/factory/live' },
      { label: 'Parte diario', path: '/factory/production/parte', also: ['/factory/production'] },
      { label: 'Pesadas y lotes', path: '/factory/product-lots/lotes', also: ['/factory/product-lots'] },
      { label: 'Pallets', path: '/factory/product-lots/pallets' },
      { label: 'Plan de máquinas', path: '/factory/schedule' },
      { label: 'Qué fabricar', path: '/factory/production/planificar' },
      { label: 'Indicadores', path: '/factory/production/indicadores' },
      { label: 'Armado afuera', path: '/factory/outwork/dia', also: ['/factory/outwork'] },
    ],
  },
  {
    id: 'materials', label: 'Materia prima', icon: <ScaleOutlined />,
    items: [
      { label: 'Recepción', path: '/factory/materials/recepcion', also: ['/factory/materials'] },
      { label: 'Mezclas', path: '/factory/materials/mezclas' },
      { label: 'Salidas a máquina', path: '/factory/production/salidas' },
      { label: 'Lotes de MP', path: '/factory/materials/lotes' },
    ],
  },
  {
    id: 'stock', label: 'Stock y depósito', icon: <WarehouseOutlined />,
    items: [
      { label: 'Producto terminado', path: '/factory/stock/terminado', also: ['/factory/stock'] },
      { label: 'Materia prima', path: '/factory/stock/mp' },
      { label: 'Dónde está cada cosa', path: '/factory/storage/donde', also: ['/factory/storage'] },
      { label: 'Traslados y conteos', path: '/factory/storage/movimientos' },
      { label: 'Movimientos de stock', path: '/stock-movements' },
    ],
  },
  {
    id: 'maintenance', label: 'Mantenimiento', icon: <BuildOutlined />,
    items: [
      { label: 'Equipos', path: '/factory/maintenance/equipos', also: ['/factory/maintenance'] },
      { label: 'Calendario', path: '/factory/maintenance/calendario' },
      { label: 'Historial', path: '/factory/maintenance/historial' },
    ],
  },
  {
    id: 'sales', label: 'Ventas', icon: <StorefrontOutlined />,
    items: [
      { label: 'Pedidos de clientes', path: '/factory/customer-orders/ordenes', also: ['/factory/customer-orders'] },
      { label: 'Clientes', path: '/customers' },
      { label: 'Precios y márgenes', path: '/factory/prices' },
    ],
  },
  {
    id: 'purchases', label: 'Compras', icon: <ShoppingCartOutlined />,
    items: [
      { label: 'Compras', path: '/purchases' },
      { label: 'Proveedores', path: '/suppliers' },
    ],
  },
  {
    id: 'numbers', label: 'Números', icon: <PaidOutlined />,
    items: [
      { label: 'Costeo', path: '/factory/costs' },
      { label: 'Ex ante vs ex post', path: '/factory/cost-close' },
      { label: 'Libro IVA', path: '/factory/tax-book' },
      { label: 'Liquidación de armado', path: '/factory/outwork/liquidacion' },
      { label: 'Deudas y planes', path: '/factory/debts' },
    ],
  },
];

const CONFIG_ITEM: NavItem = { label: 'Configuración', path: '/factory/config', also: CONFIG_PATHS };

/** El ítem del menú de una ruta: primero coincidencia exacta, si no el prefijo más largo. */
function activeItem(pathname: string): NavItem | undefined {
  const all = [PLANT_ITEM, ...GROUPS.flatMap(g => g.items), CONFIG_ITEM];
  const paths = (i: NavItem) => [i.path, ...(i.also ?? [])];
  const exact = all.find(i => paths(i).includes(pathname));
  if (exact) return exact;
  let best: { item: NavItem; len: number } | undefined;
  for (const i of all) for (const p of paths(i)) {
    if (pathname.startsWith(`${p}/`) && (!best || p.length > best.len)) best = { item: i, len: p.length };
  }
  return best?.item;
}

const OPEN_KEY = 'plant.nav.open';
const loadOpen = (): string[] => {
  try { return JSON.parse(localStorage.getItem(OPEN_KEY) || '[]'); } catch { return []; }
};

const initials = (name?: string | null) =>
  (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('') || '·';

/** PARRIPLAST con el PLAST en naranja, como en el cartel de la planta. */
const Wordmark: React.FC<{ size?: number }> = ({ size = 27 }) => (
  <Typography component="span" sx={{ fontSize: size, fontWeight: 900, letterSpacing: '-0.035em', lineHeight: 1, color: INK, whiteSpace: 'nowrap' }}>
    PARRI<Box component="span" sx={{ color: ACCENT }}>PLAST</Box>
  </Typography>
);

export const PlantLayout: React.FC = () => {
  const location = useLocation();
  // El tablero y el plano se refrescan cada minuto; en las demás pantallas alcanza con la campanita cada 5.
  const live = location.pathname === '/dashboard' || location.pathname.startsWith('/factory/plant');
  return (
    <PlantDataProvider refreshMs={live ? 60_000 : 300_000}>
      <Shell />
    </PlantDataProvider>
  );
};

const Shell: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user, tenant } = useSelector((s: RootState) => s.auth);
  const desktop = useMediaQuery('(min-width:1000px)', { noSsr: true });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userEl, setUserEl] = useState<HTMLElement | null>(null);
  const [bellEl, setBellEl] = useState<HTMLElement | null>(null);

  const layout = useMemo(() => plantLayoutForTenant(tenant), [tenant]);
  const { data, loading } = usePlantData();
  const alerts = useMemo(() => plantAlerts(data, layout), [data, layout]);
  const urgent = alerts.filter(a => a.tone === 'bad' || a.tone === 'warn').length;

  const current = activeItem(location.pathname);
  const currentGroup = GROUPS.find(g => current && g.items.includes(current))?.id;
  const [open, setOpen] = useState<string[]>(() => loadOpen());
  // El área de la pantalla actual siempre queda abierta.
  useEffect(() => {
    if (currentGroup) setOpen(o => (o.includes(currentGroup) ? o : [...o, currentGroup]));
  }, [currentGroup]);
  useEffect(() => { try { localStorage.setItem(OPEN_KEY, JSON.stringify(open)); } catch { /* sin storage */ } }, [open]);
  const toggle = (id: string) => setOpen(o => (o.includes(id) ? o.filter(x => x !== id) : [...o, id]));
  const mainRef = useRef<HTMLDivElement>(null);
  useEffect(() => { mainRef.current?.scrollTo({ top: 0 }); }, [location.pathname]);
  const go = (path: string) => {
    navigate(path);
    setMobileOpen(false);
    setUserEl(null);
    setBellEl(null);
  };
  const doLogout = () => {
    setUserEl(null);
    dispatch(logout());
    navigate('/login');
  };

  const machineZones = layout?.zones.filter(z => z.kind === 'machine' && z.id.startsWith('iny-')) ?? [];
  const running = machineZones.filter(z => {
    const m = findMachine(z, data.machines);
    return m && shiftsToday(data, m.id).length > 0;
  }).length;

  const navItem = (i: NavItem, indent: boolean) => {
    const active = current === i;
    return (
      <ButtonBase
        key={i.path}
        onClick={() => go(i.path)}
        aria-current={active ? 'page' : undefined}
        sx={{
          position: 'relative', justifyContent: 'flex-start', width: '100%', pl: indent ? 5.25 : 1.5, pr: 1.5, py: 0.8, borderRadius: 2,
          color: active ? '#fff' : '#3A3F4B', fontSize: 14.5, fontWeight: active ? 650 : 450, textAlign: 'left',
          '&:hover': { bgcolor: active ? undefined : SURFACE, color: active ? '#fff' : INK },
        }}
      >
        {active && (
          <motion.span
            layoutId="plant-nav-pill"
            style={{ position: 'absolute', inset: 0, borderRadius: 8, background: ACCENT, boxShadow: '0 6px 16px -8px rgba(240,86,29,.6)' }}
            transition={{ type: 'spring', stiffness: 520, damping: 40 }}
          />
        )}
        <Box component="span" sx={{ position: 'relative', zIndex: 1 }}>{i.label}</Box>
      </ButtonBase>
    );
  };

  const groupHeader = (label: string, icon: React.ReactNode, active: boolean, isOpen: boolean | null, onClick: () => void) => (
    <ButtonBase
      onClick={onClick}
      aria-expanded={isOpen ?? undefined}
      sx={{
        justifyContent: 'flex-start', width: '100%', gap: 1.5, px: 1.5, py: 0.95, borderRadius: 2, textAlign: 'left',
        color: INK, fontSize: 15, fontWeight: 650, '&:hover': { bgcolor: SURFACE },
        '& > svg:first-of-type': { fontSize: 21, color: active ? ACCENT : '#4B5160' },
      }}
    >
      {icon}
      <Box component="span" sx={{ flex: 1 }}>{label}</Box>
      {isOpen != null && <ExpandMore sx={{ fontSize: 19, color: MUTED, transition: 'transform .2s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />}
    </ButtonBase>
  );

  const sidebar = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* El estado en vivo de la planta, que lleva al tablero del día: es el inicio. */}
      <Box sx={{ px: 1.5, pt: 2, pb: 1 }}>
        <ButtonBase
          onClick={() => go('/dashboard')}
          sx={{
            display: 'block', width: '100%', textAlign: 'left', borderRadius: 3, p: 1.5, border: `1px solid ${location.pathname === '/dashboard' ? ACCENT : LINE}`,
            bgcolor: location.pathname === '/dashboard' ? ACCENT_SOFT : SURFACE, transition: 'border-color .2s, background-color .2s',
            '&:hover': { borderColor: ACCENT },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <PulseDot tone={running ? 'ok' : 'neutral'} size={8} pulse={!loading && running > 0} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: INK, letterSpacing: '0.02em', textTransform: 'uppercase' }}>Hoy en planta</Typography>
          </Stack>
          <Typography sx={{ mt: 0.5, fontSize: 14, color: INK, fontWeight: 600 }}>
            {machineZones.length ? `${running} de ${machineZones.length} inyectoras con parte` : 'Tablero del día'}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: urgent ? TONES.warn.fg : MUTED }}>
            {urgent ? `${urgent} aviso${urgent > 1 ? 's' : ''} para mirar` : 'Sin avisos'}
          </Typography>
        </ButtonBase>
      </Box>

      <Box component="nav" aria-label="Menú" sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1.5, pb: 1, scrollbarWidth: 'thin' }}>
        <Box sx={{ '& > button': { mb: 0.25 } }}>
          {groupHeader(PLANT_ITEM.label, <MapOutlined />, current === PLANT_ITEM, null, () => go(PLANT_ITEM.path))}
        </Box>
        {GROUPS.map(g => {
          const isOpen = open.includes(g.id);
          return (
            <Box key={g.id} sx={{ mb: 0.25 }}>
              {groupHeader(g.label, g.icon, currentGroup === g.id, isOpen, () => toggle(g.id))}
              {isOpen && <Stack spacing={0.25} sx={{ pb: 0.75 }}>{g.items.map(i => navItem(i, true))}</Stack>}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ px: 1.5, py: 1.25, borderTop: `1px solid ${LINE}` }}>
        {groupHeader(CONFIG_ITEM.label, <SettingsOutlined />, current === CONFIG_ITEM, null, () => go(CONFIG_ITEM.path))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#FBFAF8' }}>
      <SetPasswordOverlay />

      {/* Barra superior */}
      <Box
        component="header"
        sx={{
          position: 'fixed', top: 0, left: 0, right: 0, height: TOPBAR_H, zIndex: 1200, bgcolor: 'rgba(255,255,255,.92)',
          backdropFilter: 'saturate(180%) blur(12px)', borderBottom: `1px solid ${LINE}`,
          display: 'flex', alignItems: 'center', px: { xs: 1.5, md: 3 }, gap: 1.5,
        }}
      >
        {!desktop && (
          <IconButton aria-label="Abrir menú" onClick={() => setMobileOpen(true)} sx={{ color: INK }}>
            <MenuIcon />
          </IconButton>
        )}
        <ButtonBase onClick={() => go('/dashboard')} aria-label="Inicio" sx={{ gap: 2.25, borderRadius: 1.5, py: 0.5 }}>
          <Wordmark size={desktop ? 28 : 22} />
          <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: 19, color: '#2A2E36', fontWeight: 450, letterSpacing: '-0.01em' }}>
            Gestión de fábrica
          </Typography>
        </ButtonBase>

        <Box sx={{ flex: 1 }} />

        <IconButton aria-label={`Avisos (${urgent})`} onClick={e => setBellEl(e.currentTarget)} sx={{ color: INK }}>
          <Badge badgeContent={urgent} color="error" overlap="circular" sx={{ '& .MuiBadge-badge': { bgcolor: ACCENT, fontWeight: 700 } }}>
            <NotificationsNoneOutlined sx={{ fontSize: 26 }} />
          </Badge>
        </IconButton>
        <Divider orientation="vertical" flexItem sx={{ my: 2, borderColor: LINE, display: { xs: 'none', sm: 'block' } }} />
        <ButtonBase onClick={e => setUserEl(e.currentTarget)} aria-label="Cuenta" sx={{ borderRadius: 999, gap: 0.5, pl: 0.5, pr: 0.25 }}>
          <Avatar src={user?.profilePictureUrl} sx={{ width: 40, height: 40, bgcolor: '#EEF0F3', color: INK, fontSize: 15, fontWeight: 700 }}>
            {initials(user?.name)}
          </Avatar>
          <ExpandMore sx={{ color: MUTED, transition: 'transform .2s', transform: userEl ? 'rotate(180deg)' : 'none' }} />
        </ButtonBase>
      </Box>

      {/* Avisos */}
      <Popover
        open={!!bellEl}
        anchorEl={bellEl}
        onClose={() => setBellEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: 'calc(100vw - 24px)', borderRadius: 3, mt: 1, border: `1px solid ${LINE}`, boxShadow: '0 18px 40px -18px rgba(16,24,40,.35)' } } }}
      >
        <Box sx={{ px: 2.25, pt: 2, pb: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 17, letterSpacing: '-0.015em' }}>Avisos de la planta</Typography>
          <Typography sx={{ color: MUTED, fontSize: 13 }}>{alerts.length ? 'Tocá uno para ir a resolverlo.' : 'Nada para mirar por ahora.'}</Typography>
        </Box>
        <Box sx={{ maxHeight: 420, overflowY: 'auto', px: 1, pb: 1 }}>
          {alerts.map(a => (
            <ButtonBase key={a.id} onClick={() => go(a.path)} sx={{ display: 'flex', width: '100%', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 1.5, textAlign: 'left', px: 1.25, py: 1.1, borderRadius: 2, '&:hover': { bgcolor: SURFACE } }}>
              <Box sx={{ pt: 0.6 }}><PulseDot tone={a.tone} size={9} pulse={a.tone === 'bad'} /></Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 650, fontSize: 14.5, color: a.tone === 'neutral' ? INK : TONES[a.tone].fg }}>{a.title}</Typography>
                {a.detail && <Typography sx={{ fontSize: 13, color: MUTED }} noWrap>{a.detail}</Typography>}
              </Box>
            </ButtonBase>
          ))}
        </Box>
        <Divider sx={{ borderColor: LINE }} />
        <ButtonBase onClick={() => go('/dashboard')} sx={{ width: '100%', py: 1.4, color: ACCENT, fontWeight: 700, fontSize: 14 }}>
          Ver el tablero del día
        </ButtonBase>
      </Popover>

      {/* Cuenta */}
      <Menu
        anchorEl={userEl}
        open={!!userEl}
        onClose={() => setUserEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 260, borderRadius: 3, mt: 1, border: `1px solid ${LINE}`, boxShadow: '0 18px 40px -18px rgba(16,24,40,.35)' } } }}
      >
        <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
          <Typography sx={{ fontWeight: 700 }} noWrap>{user?.name}</Typography>
          <Typography sx={{ fontSize: 13, color: MUTED }} noWrap>{user?.email}</Typography>
          {tenant?.name && <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.25 }} noWrap>{tenant.name}</Typography>}
        </Box>
        <Divider sx={{ borderColor: LINE }} />
        <MenuItem onClick={() => go('/users')}><ListItemIcon><PeopleOutline fontSize="small" /></ListItemIcon>Usuarios</MenuItem>
        <MenuItem onClick={() => go('/settings')}><ListItemIcon><SettingsOutlined fontSize="small" /></ListItemIcon>Datos de la empresa</MenuItem>
        <Divider sx={{ borderColor: LINE }} />
        <MenuItem onClick={doLogout}><ListItemIcon><Logout fontSize="small" /></ListItemIcon>Cerrar sesión</MenuItem>
      </Menu>

      {/* Menú lateral */}
      {desktop ? (
        <Box
          component="aside"
          sx={{ position: 'fixed', top: TOPBAR_H, left: 0, bottom: 0, width: SIDEBAR_W, bgcolor: '#fff', borderRight: `1px solid ${LINE}`, zIndex: 1100 }}
        >
          {sidebar}
        </Box>
      ) : (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{ sx: { width: 270, pt: 1 } }}
        >
          <Box sx={{ px: 3, pt: 2, pb: 1 }}><Wordmark size={24} /></Box>
          {sidebar}
        </Drawer>
      )}

      {/* Contenido */}
      {/* El contenido scrollea solo (como en AdminLayout): así lo "sticky" de cada pantalla queda bajo la barra. */}
      <Box
        component="main"
        ref={mainRef}
        sx={{ mt: `${TOPBAR_H}px`, ml: desktop ? `${SIDEBAR_W}px` : 0, height: `calc(100vh - ${TOPBAR_H}px)`, overflowY: 'auto', overflowX: 'hidden', minWidth: 0 }}
      >
        {/* Cada pantalla entra con un fundido corto (sin salida, para no mostrar la nueva dentro de la vieja). */}
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
        >
          <FlatNavContext.Provider value>
            <Outlet />
          </FlatNavContext.Provider>
        </motion.div>
      </Box>
    </Box>
  );
};


export default PlantLayout;
